import {
  Skeleton,
  Typography,
  type TypographySize,
  type TypographyWeight,
} from '@repo/ui/components'
import type { CSSVariableColor } from '@repo/ui/types'
import { useTranslate } from '../..'
import { formatNumberWithCommas } from '@repo/tools'

// Function to truncate decimal places without rounding and remove trailing zeros
const truncateDecimals = (num: number, decimals: number): string => {
  const numStr = num.toString()
  const isNegative = num < 0
  const absNum = Math.abs(num)

  // For very large/small numbers or numbers in scientific notation, use string manipulation for precision
  if (
    absNum > Number.MAX_SAFE_INTEGER ||
    absNum < Number.MIN_SAFE_INTEGER ||
    numStr.includes('e') ||
    decimals > 15
  ) {
    // Use string-based approach for high precision
    const fullPrecisionStr = absNum.toString()

    // If it's already in scientific notation, convert it to decimal
    let decimalStr: string
    if (fullPrecisionStr.includes('e')) {
      decimalStr = absNum.toFixed(20).replace(/0+$/, '').replace(/\.$/, '')
    } else {
      decimalStr = fullPrecisionStr
    }

    const parts = decimalStr.split('.')
    const integerPart = parts[0] || '0'
    const decimalPart = parts[1] || ''

    // Truncate decimal part to requested precision
    const truncatedDecimal = decimalPart.substring(0, decimals)

    // Remove trailing zeros from decimal part
    const cleanedDecimal = truncatedDecimal.replace(/0+$/, '')

    // Combine parts
    let result = integerPart
    if (cleanedDecimal) {
      result += '.' + cleanedDecimal
    }

    return isNegative ? '-' + result : result || '0'
  }

  // For normal numbers, use the existing logic but remove trailing zeros
  const factor = Math.pow(10, decimals)
  const truncated = Math.floor(absNum * factor) / factor
  const result = isNegative ? -truncated : truncated

  // Convert to string and remove trailing zeros
  const resultStr = result.toString()

  // If it's an integer, return without decimal point
  if (
    resultStr.includes('.') &&
    resultStr.split('.')[1]?.replace(/0+$/, '') === ''
  ) {
    return resultStr.split('.')[0] || ''
  }

  // Remove trailing zeros from decimal part
  return resultStr.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
}

// Function to format value with scaling, decimal places, and comma separation
export const formatValueWithScale = (
  value: number | string,
  scale: number,
  decimalPlaces: number,
): string => {
  // Convert value to number and divide by scale
  const numericValue = Number(value) / scale

  // If decimalPlaces is -1, show full precision without truncation
  if (decimalPlaces === -1) {
    // Convert to string and remove trailing zeros
    let fullPrecision = numericValue.toString()

    // Remove trailing zeros from decimal part
    fullPrecision = fullPrecision
      .replace(/(\.\d*?)0+$/, '$1')
      .replace(/\.$/, '')

    return formatNumberWithCommas(fullPrecision)
  }

  // Format with decimal places and remove trailing zeros
  const formattedValue = truncateDecimals(numericValue, decimalPlaces)

  // Add comma separation
  return formatNumberWithCommas(formattedValue)
}

interface IValueWithUnitProps {
  id?: string
  // value
  value?: number | string
  valueSize?: TypographySize
  valueColor?: CSSVariableColor
  valueWeight?: TypographyWeight
  valueClassName?: string
  // unit
  unit?: string // 'percentage' | 'crypto' | 'property' | 'IRR' | 'TMN' | 'cm2' | 'm2' | 'SQ2' | 'square_token'
  unitSize?: TypographySize
  unitColor?: CSSVariableColor
  unitWeight?: TypographyWeight
  unitClassName?: string
  // global
  className?: string
  scale?: number
  decimalPlaces?: number // Use -1 to show full precision without truncation, supports up to 18 decimal places
  approximately?: boolean
  showZero?: boolean
  betweenChildren?: React.ReactNode | string
  unitPosition?: 'after' | 'before'
  loading?: boolean
  loadingSize?: string
}

export const ValueWithUnit = (props: IValueWithUnitProps) => {
  const {
    id,
    value,
    valueSize,
    valueColor = 'text-neutral-surface-100',
    valueWeight = 'bold',
    valueClassName,
    unit,
    unitSize = '2xs',
    unitColor = 'text-neutral-surface-80',
    unitWeight,
    unitClassName,
    className,
    scale = 1,
    decimalPlaces = 18,
    approximately = false,
    showZero = true,
    betweenChildren,
    unitPosition = 'after',
    loading,
    loadingSize = 'w-22 h-md',
  } = props

  const { t } = useTranslate()

  if (loading) return <Skeleton className={loadingSize} />

  return (
    <span id={id} className={`flex items-center gap-x-3xs ${className}`}>
      {/* ({id}) */}
      {unit && unitPosition === 'before' && (
        <Typography
          variant='span'
          size={unitSize}
          color={unitColor}
          weight={unitWeight}
          className={`mt-[1px] ${unitClassName}`}
        >
          {t(`value_with_unit_${unit}`)}
        </Typography>
      )}
      <Typography
        variant='span'
        size={valueSize}
        color={valueColor}
        weight={valueWeight}
        className={valueClassName}
      >
        {value
          ? value !== Infinity
            ? formatValueWithScale(value || 0, scale, decimalPlaces)
            : '∞'
          : showZero
            ? '0'
            : '-'}
      </Typography>
      {approximately && <span className='text-neutral-surface-80'>~</span>}
      {betweenChildren}
      {unit && unitPosition === 'after' && (
        <Typography
          variant='span'
          size={unitSize}
          color={unitColor}
          weight={unitWeight}
          className={`mt-[1px] ${unitClassName}`}
        >
          {t(`value_with_unit_${unit}`)}
        </Typography>
      )}
    </span>
  )
}
